import { FormEvent, useEffect, useId, useState } from 'react'
import useEscapeKey from '../hooks/useEscapeKey'
import { contentAPI, creatorPostsAPI } from '../services/api'
import type { Content, ContentExport, CreatorPost } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import PostImagePicker from './PostImagePicker'
import Spinner from './Spinner'

interface PostYourselfDialogProps {
  content: Content
  onClose: () => void
  /** "I posted it": the link was saved. */
  onPosted: (post: CreatorPost) => void
  /** Offer an image to post with the text (not for a clip: the video is the post). */
  withImage?: boolean
}

/**
 * Posting a draft on your own account. FinCreator doesn't post for you: copy
 * the text (already checked, with "Not financial advice" added if it was
 * missing), open the network, post it, then paste the post's link back so
 * its views and likes can be followed.
 */
const PostYourselfDialog = ({ content, onClose, onPosted, withImage = true }: PostYourselfDialogProps) => {
  const titleId = useId()
  const [exported, setExported] = useState<ContentExport | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [copied, setCopied] = useState<'text' | 'hashtags' | null>(null)
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEscapeKey(onClose, !saving)

  useEffect(() => {
    let stopped = false
    contentAPI
      .exportFor(content.id)
      .then((e) => !stopped && setExported(e))
      .catch((e) => !stopped && setProblem(apiErrorMessage(e, "Couldn't prepare this post.")))
    return () => {
      stopped = true
    }
  }, [content.id])

  const copy = async (what: 'text' | 'hashtags', value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(what)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    try {
      onPosted(await creatorPostsAPI.add({ url: url.trim(), content_id: content.id }))
    } catch (err) {
      setSaveError(apiErrorMessage(err, "Couldn't save the link."))
    } finally {
      setSaving(false)
    }
  }

  const openUrl = exported?.compose_url || exported?.upload_url

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 id={titleId} className="text-lg font-bold text-gray-900">
          Post it yourself{exported ? ` on ${exported.network_name}` : ''}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Post from your own account, then paste the post's link here to follow how it does.
        </p>

        {!exported && !problem && (
          <div className="mt-4 flex justify-center">
            <Spinner size="sm" />
          </div>
        )}
        {problem && (
          <p role="alert" className="mt-4 text-sm text-danger-700">
            {problem}
          </p>
        )}

        {exported && (
          <div className="mt-4 space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">1. Copy the post</span>
                <button
                  type="button"
                  onClick={() => copy('text', exported.text)}
                  className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {copied === 'text' ? 'Copied' : 'Copy text'}
                </button>
              </div>
              <textarea
                readOnly
                aria-label="Post text"
                value={exported.text}
                rows={5}
                className="mt-2 w-full rounded-md border border-gray-300 p-2 text-sm text-gray-800"
              />
              {exported.hashtags && (
                <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-600">
                  <span className="truncate">{exported.hashtags}</span>
                  <button
                    type="button"
                    onClick={() => copy('hashtags', exported.hashtags || '')}
                    className="shrink-0 rounded-md border border-gray-300 px-2 py-0.5 text-gray-700 hover:bg-gray-50"
                  >
                    {copied === 'hashtags' ? 'Copied' : 'Copy hashtags'}
                  </button>
                </div>
              )}
            </div>

            {withImage && <PostImagePicker contentId={content.id} network={exported.network} />}

            {openUrl && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">2. Post it</span>
                <a
                  href={openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                >
                  {exported.compose_url ? `Open ${exported.network_name} with this post` : `Open ${exported.network_name}`}
                </a>
              </div>
            )}

            <form onSubmit={save} className="space-y-2">
              <label className="block text-sm font-medium text-gray-900" htmlFor={`${titleId}-url`}>
                3. Posted it? Paste the link to the post
              </label>
              <div className="flex gap-2">
                <input
                  id={`${titleId}-url`}
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={`https://… (your ${exported.network_name} post)`}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
                <button
                  type="submit"
                  disabled={saving || !url.trim()}
                  className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'I posted it'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {exported.can_fetch_stats
                  ? `${exported.network_name} views and likes are read automatically.`
                  : `${exported.network_name} doesn't share counts with FinCreator, so you can type them in on the post.`}
              </p>
              {saveError && (
                <p role="alert" className="text-sm text-danger-700">
                  {saveError}
                </p>
              )}
            </form>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default PostYourselfDialog
